import React, { useEffect, useState } from 'react';
import { apiFetch } from '../App';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, AlertTriangle, Loader2, RefreshCw, PlugZap } from 'lucide-react';

/*
 * AdminPayPalStatus
 * ─────────────────
 * Small deploy-time sanity card so the admin can visit /admin right after
 * a Manage Deployments change and see whether the running backend picked
 * up the LIVE PayPal creds — WITHOUT SSHing anywhere and without ever
 * seeing the secret itself.
 */
const Row = ({ label, value, ok }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <span className="text-zinc-400">{label}</span>
    <span className="flex items-center gap-2 font-mono text-zinc-200">
      {ok === true && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
      {ok === false && <XCircle className="h-4 w-4 text-red-400" />}
      {value}
    </span>
  </div>
);

const previewKey = (v, keep = 4) => {
  if (!v) return null;
  if (v.length <= keep * 2) return '*'.repeat(v.length);
  return `${v.slice(0, keep)}…${v.slice(-keep)}`;
};

const AdminPayPalStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/admin/paypal-status');
      setStatus(data);
    } catch (err) {
      toast.error(`Could not read PayPal status: ${err?.message || 'unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await apiFetch('/admin/paypal-status/verify', { method: 'POST' });
      setVerifyResult(data);
      if (data?.ok) toast.success('PayPal credentials authenticate successfully');
      else toast.error('PayPal rejected the credentials');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'unknown error';
      toast.error(`Verify failed: ${detail}`);
      setVerifyResult({ ok: false, error: detail });
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => { load(); }, []);

  const badgeFor = (s) => {
    if (s === 'live') return { text: 'LIVE', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50' };
    if (s === 'sandbox') return { text: 'SANDBOX', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/50' };
    return { text: 'UNCONFIGURED', cls: 'bg-red-500/20 text-red-300 border-red-500/50' };
  };
  return (
    <div className="glass-card p-6" data-testid="admin-paypal-status">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <PlugZap className="h-5 w-5 text-purple-400" />
          PayPal env sanity
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="paypal-status-refresh-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>
      <p className="text-zinc-400 text-sm mb-4">
        Reads the env vars the running backend actually sees. The secret is never returned — only presence.
      </p>

      {status ? (
        <>
          <div className="flex items-center gap-3 mb-4">
            <span
              className={`px-3 py-1 rounded-full text-xs font-bold border ${badgeFor(status.status).cls}`}
              data-testid="paypal-status-badge"
            >
              {badgeFor(status.status).text}
            </span>
            <span className="text-xs text-zinc-500">PAYPAL_MODE = <code data-testid="paypal-status-mode">{status.mode}</code></span>
          </div>

          <div className="divide-y divide-white/5">
            <Row label="Backend PAYPAL_CLIENT_ID"
                 value={status.client_id_preview || 'not set'}
                 ok={status.client_id_set} />
            <Row label="Backend PAYPAL_CLIENT_SECRET"
                 value={status.secret_set ? 'set' : 'not set'}
                 ok={status.secret_set} />
            <Row label="Frontend REACT_APP_PAYPAL_CLIENT_ID"
                 value={previewKey(process.env.REACT_APP_PAYPAL_CLIENT_ID) || 'not set'}
                 ok={Boolean(process.env.REACT_APP_PAYPAL_CLIENT_ID)} />
            <Row label="ENABLE_TEST_CREDITS (backend)"
                 value={status.test_credits_enabled ? 'enabled' : 'disabled'}
                 ok={!status.test_credits_enabled || status.mode !== 'live'} />
          </div>

          {status.warnings?.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="paypal-status-warnings">
              {status.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 pt-5 border-t border-white/10">
            <div className="flex items-center gap-3">
              <Button
                onClick={verify}
                disabled={verifying || !status.client_id_set || !status.secret_set}
                data-testid="paypal-status-verify-btn"
                className="flex items-center gap-2"
              >
                {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
                Verify credentials with PayPal
              </Button>
              <span className="text-xs text-zinc-500">Calls PayPal&apos;s OAuth2 endpoint — no purchase is made.</span>
            </div>

            {verifyResult && (
              <div
                className={`mt-4 p-3 rounded-md border text-sm ${verifyResult.ok
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
                  : 'bg-red-500/10 border-red-500/30 text-red-200'}`}
                data-testid="paypal-status-verify-result"
              >
                {verifyResult.ok ? (
                  <>
                    <div className="font-medium mb-1">✓ Authenticated against {verifyResult.endpoint}</div>
                    <div className="text-xs opacity-80">
                      scopes: {verifyResult.scopes?.length || 0} &middot; token TTL: {verifyResult.expires_in}s
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium mb-1">✗ PayPal rejected the credentials</div>
                    <div className="text-xs opacity-80">
                      {verifyResult.endpoint ? <>endpoint: <code>{verifyResult.endpoint}</code> &middot; </> : null}
                      {verifyResult.http_status ? <>http: {verifyResult.http_status} &middot; </> : null}
                      {verifyResult.error}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2 text-zinc-400 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading PayPal status…
        </div>
      )}
    </div>
  );
};

export default AdminPayPalStatus;
